import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useEtiquetaCritico, useNivelLabels } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp, TrendingDown, FileCheck, AlertTriangle, Bell, CheckCircle2, XCircle, Trophy, Upload, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import OnboardingWizard from "@/components/OnboardingWizard";
import { OnboardingAsistido } from "@/components/OnboardingAsistido";
import { resumenCobertura } from "@/lib/cobertura";
import { verifyAttrs } from "@/verify/contract";

function Ring({ verified = 0, pending = 0, readiness = 0 }) {
  const v = Math.max(0, Math.min(100, verified));
  const p = Math.max(0, Math.min(100 - v, pending));
  const vDeg = v * 3.6;
  const pDeg = (v + p) * 3.6;
  const solid = readiness >= 85 ? "#10B981" : "#FF5625";
  const bg = `conic-gradient(${solid} 0deg ${vDeg}deg, ${solid}40 ${vDeg}deg ${pDeg}deg, #E2E8F0 ${pDeg}deg 360deg)`;
  return (
    <div className="relative w-40 h-40" data-testid="readiness-ring">
      <div className="w-full h-full rounded-full" style={{ background: bg }} />
      <div className="absolute inset-3 bg-white rounded-full flex flex-col items-center justify-center">
        <div className="text-4xl font-black tracking-tighter text-[#0E2A47]">{readiness}%</div>
        <div className="text-[10px] uppercase tracking-widest text-[#0E2A47]/50">Readiness</div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="p-6 flex flex-col justify-between border-[#E2E8F0]">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#0E2A47]/50">{label}</span>
        <Icon size={18} className={accent ? "text-[#FF5625]" : "text-[#0E2A47]/40"} />
      </div>
      <div className="mt-4">
        <div className="text-4xl font-black tracking-tighter text-[#0E2A47]">{value}</div>
        {sub && <div className="text-xs text-[#0E2A47]/50 mt-1">{sub}</div>}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user, tenant, scopeTenant } = useAuth();
  const navigate = useNavigate();
  const kritPrimary = useEtiquetaCritico();
  const nivelLabelsPrimary = useNivelLabels();
  const normas = tenant?.normas_config || [];
  const multiNorma = normas.length > 1;
  const [normaSel, setNormaSel] = useState(tenant?.norma_activa || normas[0]?.codigo || null);
  const [scoring, setScoring] = useState(null);
  const [cob, setCob] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [hitos, setHitos] = useState([]);
  const [docsCount, setDocsCount] = useState(null);
  const [chartReady, setChartReady] = useState(false);
  const needsScope = user.rol === "superadmin" && !scopeTenant;

  const configSel = multiNorma ? normas.find((n) => n.codigo === normaSel) : null;
  const krit = configSel?.etiqueta_critico || kritPrimary;
  const nivelLabels = configSel
    ? ["DOCUMENTA", "REGISTRA", "TRAZA", configSel.etiqueta_nivel_4 || "CERTIFICA"]
    : nivelLabelsPrimary;

  useEffect(() => {
    const t = requestAnimationFrame(() => setChartReady(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    if (!normaSel && normas.length) setNormaSel(tenant?.norma_activa || normas[0]?.codigo);
  }, [normaSel, normas, tenant]);

  const load = useCallback(async () => {
    if (needsScope) return;
    const ev = await api.post("/hitos/evaluar");
    (ev.data.nuevos || []).forEach((h) => toast.success("¡Hito desbloqueado! 🏆", { description: h.titulo }));
    const scoringUrl = multiNorma && normaSel ? `/scoring?norma=${encodeURIComponent(normaSel)}` : "/scoring";
    const [s, a, h] = await Promise.all([api.get(scoringUrl), api.get("/alertas"), api.get("/hitos")]);
    setScoring(s.data);
    setAlertas(a.data);
    setHitos(h.data);
    try { const c = await api.get("/norma/cobertura"); setCob(c.data); } catch { /* cobertura opcional */ }
    try {
      const r = await api.get("/cuenta/resumen");
      setDocsCount(r.data?.documentos ?? 1);
    } catch { setDocsCount(1); }
  }, [needsScope, multiNorma, normaSel]);

  useEffect(() => { load(); }, [load]);

  if (needsScope)
    return <div className="text-[#0E2A47]/60" data-testid="dashboard-no-scope">Seleccione un tenant en la sección <b>Tenants</b> para ver su panel.</div>;
  if (!scoring) return <div className="text-[#0E2A47]/60">Cargando panel…</div>;

  const chartData = scoring.niveles.map((n) => ({
    name: nivelLabels[n.nivel - 1] || `Nivel ${n.nivel}`, progreso: n.progreso, activo: n.activo,
  }));

  const docsEmpty = docsCount === 0;

  return (
    <div data-testid="dashboard">
      {docsEmpty && (
        <Card className="p-8 sm:p-10 border-0 bg-[#0E2A47] text-white mb-6 relative overflow-hidden" data-testid="dashboard-empty-hero">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FF5625] mb-3"><Sparkles size={14} /> Empieza aquí</div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter leading-tight max-w-2xl">
            Sube tu primer documento y te digo en segundos qué cubre y qué te falta
          </h1>
          <p className="mt-3 text-white/70 max-w-xl">
            Sube tu Plan APPCC (o cualquier documento) y la IA lo clasifica contra tu norma al instante: verás qué requisitos cubres y cuáles te quedan. Sin plantillas ni formularios.
          </p>
          <Button onClick={() => navigate("/subir-ia")} data-testid="dashboard-empty-subir"
            className="mt-6 bg-[#FF5625] hover:bg-[#E0451A] text-white font-semibold px-8 h-12 text-base">
            <Upload size={18} className="mr-2" /> Subir mi primer documento <ArrowRight size={16} className="ml-2" />
          </Button>
          <OnboardingAsistido variant="hero" />
        </Card>
      )}
      <OnboardingWizard />
      {!docsEmpty && (<>
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5625] mb-1">Panel de control</div>
        <h1 className="text-3xl font-black tracking-tighter text-[#0E2A47]">Dashboard de Readiness</h1>
      </div>

      {multiNorma && (
        <div className="mb-6" data-testid="norma-tabs">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-[#0E2A47]/50 mb-2">
            Doble certificación · elige la lente de evaluación
          </div>
          <Tabs value={normaSel} onValueChange={(v) => { setScoring(null); setNormaSel(v); }}>
            <TabsList>
              {normas.map((n) => (
                <TabsTrigger key={n.codigo} value={n.codigo} data-testid={`norma-tab-${n.codigo}`}>
                  {n.nombre_corto || n.nombre}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="lg:col-span-1 p-6 flex flex-col items-center justify-center border-[#E2E8F0]">
          <Ring verified={cob ? cob.cobertura_pct : scoring.coverage_pct}
                pending={cob ? Math.round((cob.con_reservas / (cob.total || 1)) * 1000) / 10 : 0}
                readiness={scoring.readiness} />
          <div className="mt-3 flex items-center gap-3 text-[10px] text-[#0E2A47]/60">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#FF5625]" />Verificado {cob ? cob.cubiertos : scoring.requisitos_cubiertos}</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FF562540" }} />Pendiente {cob ? cob.con_reservas : 0}</span>
          </div>
          {scoring.gaps.ko_sin_evidencia > 0 && (
            <div className="mt-2 text-center text-xs text-[#0E2A47]/70" data-testid="readiness-bloqueo">
              Readiness bloqueado: <b>{scoring.gaps.ko_sin_evidencia} {krit} sin evidencia</b> restan {scoring.gaps.ko_sin_evidencia * 15} pts a tu {cob ? cob.cobertura_pct : scoring.coverage_pct}% verificado.
            </div>
          )}
          {scoring.gaps.ko === 0 && (
            <div className="mt-3 flex items-center gap-2">
              <Badge className="bg-[#10B981] hover:bg-[#10B981]"><CheckCircle2 size={13} className="mr-1" />Sin {krit} abiertos</Badge>
            </div>
          )}
          {(() => {
            const koPend = cob ? cob.capitulos.reduce((a, c) => a + c.requisitos.filter((r) => r.es_critico && r.pendiente_confirmacion).length, 0) : 0;
            return koPend > 0 ? (
              <Button onClick={() => navigate("/norma?revisar=ko")} data-testid="cta-revisar-ko"
                className="mt-3 w-full max-w-full text-sm px-2 py-2 h-auto whitespace-normal leading-tight bg-[#FF5625] hover:bg-[#E0451A]">
                Revisar {koPend} {krit} pendiente{koPend === 1 ? "" : "s"} <ArrowRight size={14} className="ml-1 shrink-0" />
              </Button>
            ) : null;
          })()}
          {tenant?.diagnostico_estimado && (!multiNorma || normaSel === "SANIDAD_ES") && (
            <div className="mt-4 w-full rounded-lg bg-[#0E2A47]/[0.04] border border-[#E2E8F0] px-3 py-2 text-center" data-testid="punto-de-partida">
              <div className="text-[10px] uppercase tracking-widest text-[#0E2A47]/40">Punto de partida (estimado)</div>
              <div className="flex items-center justify-center gap-2 mt-0.5">
                <span className="text-lg font-black text-[#0E2A47]/70" data-testid="punto-partida-valor">{tenant.diagnostico_estimado.readiness}%</span>
                {(() => {
                  const delta = scoring.readiness - tenant.diagnostico_estimado.readiness;
                  if (delta > 0) return <span className="text-xs font-semibold text-[#10B981] flex items-center gap-0.5" data-testid="punto-partida-delta"><TrendingUp size={12} />+{delta} pts reales</span>;
                  if (delta < 0) return <span className="text-xs font-semibold text-[#F59E0B] flex items-center gap-0.5" data-testid="punto-partida-delta"><TrendingDown size={12} />{delta} pts</span>;
                  return <span className="text-xs text-[#0E2A47]/40" data-testid="punto-partida-delta">= igual</span>;
                })()}
              </div>
              <div className="text-[10px] text-[#0E2A47]/40 mt-0.5">Tu estimación inicial vs. tu readiness real</div>
            </div>
          )}
        </Card>

        <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Card className="p-6 flex flex-col justify-between border-[#E2E8F0]" data-testid="kpi-cobertura"
            {...(cob ? verifyAttrs("CoberturaRequisitos", resumenCobertura(cob)) : {})}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#0E2A47]/50">Cobertura verificada</span>
              <FileCheck size={18} className="text-[#0E2A47]/40" />
            </div>
            <div className="mt-4">
              <div className="text-4xl font-black tracking-tighter text-[#0E2A47]">{cob ? cob.cobertura_pct : scoring.coverage_pct}%</div>
              {cob ? (
                <div className="text-xs text-[#0E2A47]/50 mt-1">
                  {cob.cubiertos}/{cob.total} cubiertos · <span className="text-[#B45309]">{cob.con_reservas} reservas</span> · <span className="text-[#EF4444]">{cob.sin_evidencia} sin evidencia</span>
                </div>
              ) : (
                <div className="text-xs text-[#0E2A47]/50 mt-1">{scoring.requisitos_cubiertos}/{scoring.requisitos_total} requisitos</div>
              )}
            </div>
          </Card>
          <Kpi icon={AlertTriangle} label={`${krit} abiertos`} value={scoring.gaps.ko} sub={krit === "KO" ? "Bloquean certificación" : "Bloquean el cumplimiento"} accent={scoring.gaps.ko > 0} />
          <Kpi icon={TrendingUp} label="No conformidades registradas" value={scoring.gaps.abiertos} sub={`M:${scoring.gaps.mayor} · m:${scoring.gaps.menor}`} />
          <Kpi icon={Bell} label="Alertas" value={alertas.length} sub="Activas" accent={alertas.length > 0} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Card className="lg:col-span-2 p-6 border-[#E2E8F0]">
          <h3 className="text-lg font-bold tracking-tight text-[#0E2A47] mb-4">Progreso por nivel</h3>
          <div className="space-y-4">
            {scoring.niveles.map((n) => (
              <div key={n.nivel} data-testid={`nivel-progress-${n.nivel}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-[#0E2A47]">
                    Nivel {n.nivel} · {nivelLabels[n.nivel - 1]} {!n.activo && <span className="text-[#0E2A47]/40">(inactivo)</span>}
                  </span>
                  <span className="text-[#0E2A47]/60">{n.cubiertos}/{n.requisitos_total} · {n.progreso}%</span>
                </div>
                <Progress value={n.progreso} className="h-2" />
              </div>
            ))}
          </div>
          <div className="h-40 mt-6 w-full">
            {chartReady && (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#0E2A47" }} axisLine={false} tickLine={false} />
                <YAxis hide domain={[0, 100]} />
                <Bar dataKey="progreso" radius={[4, 4, 0, 0]}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={d.activo ? "#0E2A47" : "#CBD5E1"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="p-6 border-[#E2E8F0]">
          <h3 className="text-lg font-bold tracking-tight text-[#0E2A47] mb-4">Alertas proactivas</h3>
          {alertas.length === 0 ? (
            <div className="text-sm text-[#0E2A47]/50">Sin alertas activas.</div>
          ) : (
            <div className="space-y-3">
              {alertas.slice(0, 6).map((a) => (
                <div key={a.id} className="flex gap-3 items-start" data-testid={`alerta-item-${a.id}`}>
                  <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${a.severidad === "critica" ? "bg-[#EF4444]" : a.severidad === "alta" ? "bg-[#F59E0B]" : "bg-[#94A3B8]"}`} />
                  <div className="text-sm text-[#0E2A47]/80 leading-snug">{a.mensaje}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6 border-[#E2E8F0] mt-6" data-testid="camino-recorrido">
        <h3 className="text-lg font-bold tracking-tight text-[#0E2A47] mb-4 flex items-center gap-2">
          <Trophy size={18} className="text-[#FF5625]" /> Camino recorrido
        </h3>
        {hitos.length === 0 ? (
          <div className="text-sm text-[#0E2A47]/50">Aún no hay hitos conseguidos. ¡Empieza subiendo documentos!</div>
        ) : (
          <div className="flex flex-wrap gap-4">
            {hitos.map((h) => (
              <div key={h.id} className="flex items-center gap-3 bg-[#F7F8FA] rounded-lg px-4 py-3 border border-[#E2E8F0]" data-testid={`hito-${h.codigo}`}>
                <div className="w-9 h-9 rounded-full bg-[#FF5625]/10 flex items-center justify-center shrink-0">
                  <Trophy size={16} className="text-[#FF5625]" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#0E2A47]">{h.titulo}</div>
                  <div className="text-xs text-[#0E2A47]/50">{new Date(h.conseguido_en).toLocaleDateString("es-ES")}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      </>)}
    </div>
  );
}
